
import React from 'react';
import { MicIcon, StopIcon, ChevronDownIcon, ChevronUpIcon } from './icons';

interface VoiceControlUIProps {
  isListening: boolean;
  isSpeaking: boolean;
  isProcessing: boolean;
  userTranscript: string;
  aiTranscript: string;
  onToggleListening: () => void;
  permissionState: PermissionState | 'prompt' | 'denied' | 'granted' | null;
  isVisible: boolean;
  onToggleVisibility: () => void;
}

const VoiceControlUI: React.FC<VoiceControlUIProps> = ({
  isListening,
  isSpeaking,
  isProcessing,
  userTranscript,
  aiTranscript,
  onToggleListening,
  permissionState,
  isVisible,
  onToggleVisibility,
}) => {
  let statusText = "Click the mic to start";
  let statusColor = "text-gray-500 dark:text-gray-400";
  if (permissionState === 'denied') {
    statusText = "Microphone access denied";
    statusColor = "text-red-500 dark:text-red-400";
  } else if (isListening && !isProcessing && !isSpeaking) {
    statusText = "Listening...";
    statusColor = "text-blue-500 dark:text-blue-400";
  } else if (isProcessing) {
    statusText = "Thinking...";
    statusColor = "text-yellow-500 dark:text-yellow-400";
  } else if (isSpeaking) {
    statusText = "Speaking...";
    statusColor = "text-green-500 dark:text-green-400";
  } else if (!isListening && permissionState === 'granted') {
    statusText = "Session ended. Click to restart.";
  }

  const pulseClass = isListening && !isProcessing && !isSpeaking ? 'animate-pulse' : '';

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-40 transition-transform duration-300 ease-in-out ${isVisible ? 'translate-y-0' : 'translate-y-[calc(100%-32px)]'}`}>
        <div className="w-full flex justify-center">
            <button
                onClick={onToggleVisibility}
                className="bg-white dark:bg-gray-800 border-t border-l border-r border-gray-200 dark:border-gray-700 rounded-t-lg px-6 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                aria-label={isVisible ? "Hide voice controls" : "Show voice controls"}
            >
                {isVisible ? <ChevronDownIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" /> : <ChevronUpIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
            </button>
        </div>
        <div className={`bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 shadow-t-xl`}>
            <div className="container mx-auto px-4 py-3 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="w-full md:w-1/3 flex items-center justify-center md:justify-start">
                    <div className={`text-center md:text-left transition-colors duration-300 ${statusColor}`}>
                        <p className="font-semibold text-lg">{statusText}</p>
                    </div>
                </div>

                <div className="w-full md:w-auto flex justify-center">
                    <button
                        onClick={onToggleListening}
                        disabled={permissionState === 'denied'}
                        className={`rounded-full w-20 h-20 flex items-center justify-center transition-all duration-300 shadow-lg transform hover:scale-110 focus:outline-none focus:ring-4 ${
                            isListening
                            ? 'bg-red-500 hover:bg-red-600 focus:ring-red-300 dark:focus:ring-red-800'
                            : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-300 dark:focus:ring-indigo-800'
                        } ${pulseClass} disabled:bg-gray-400 disabled:cursor-not-allowed disabled:scale-100`}
                        aria-label={isListening ? "Stop listening" : "Start listening"}
                    >
                        {isListening ? (
                        <StopIcon className="w-10 h-10 text-white" />
                        ) : (
                        <MicIcon className="w-10 h-10 text-white" />
                        )}
                    </button>
                </div>

                <div className="w-full md:w-1/3 text-sm text-center md:text-right">
                    <p className="text-gray-700 dark:text-gray-300 italic truncate">You: {userTranscript || "..."}</p>
                    <p className="text-indigo-700 dark:text-indigo-400 font-semibold truncate">Gemini: {aiTranscript || "..."}</p>
                </div>
            </div>
        </div>
    </div>
  );
};

export default VoiceControlUI;
